import {
  LayoutDashboardIcon, LineChartIcon, BoxesIcon, HelpCircleIcon, SlidersHorizontalIcon,
  UsersRoundIcon, UserIcon, BellIcon, LifeBuoyIcon, SettingsIcon, PlayIcon, ShieldCheckIcon,
  ClipboardListIcon, MicroscopeIcon,
  type LucideIcon,
} from 'lucide-react';

import type { StringKey } from '@/lib/strings/en';

/**
 * Sidebar structure, taken verbatim from NAFIS_Assessment_Admin_Walkthrough.html.
 *
 * That file is the console stakeholders were shown, so its groups, labels and
 * ordering are the contract for this nav — including the nested Questions
 * group and the three items pinned to the bottom.
 */
/**
 * NOTE: there is deliberately no `phase` field here.
 *
 * There was one — "the build phase that makes this real" — and nothing rendered
 * it and nothing updated it. It said Phase 2 beside Scoring Engine and Phase 3
 * beside Candidates long after both shipped, and Phase 4 beside Settings for a
 * phase that never touched that screen. Unrendered, unmaintained metadata that
 * reads as authoritative is worse than none: the roadmap lives in
 * docs/PROGRESS.md and docs/build/07-build-phases.md, which are updated as part
 * of finishing a phase.
 *
 * A screen that is not built says so on the screen itself (pages/screens.tsx),
 * where a reader can see it.
 */
/**
 * NOTE ALSO: titles here are **string keys, not text** (Workstream H1).
 *
 * The nav is data, and data that carried English would make the sidebar the one
 * part of the console that could not be translated without editing a structure
 * file. `title` is typed as `StringKey`, so a nav entry pointing at a string that
 * does not exist fails the build rather than rendering its own key.
 */
/**
 * A live count rendered beside a nav item.
 *
 * A NAME, not a number and not a fetcher: the nav is data, and a data file that
 * held a query would make the sidebar the one place a request is issued from a
 * structure definition. `layout.tsx` maps the name to the hook that counts.
 *
 * **The bar for adding one is that the number is REAL.** `lib/notifications.ts`
 * records what happens otherwise — six invented rows behind a "Sample data"
 * label, which the first operator to see them reported as broken. `qcQueue`
 * counts results the server is actually withholding, from
 * `GET /api/results/qc-queue`, and reads zero when it cannot find out.
 */
export type NavBadge = 'qcQueue';

export interface NavItem {
  title: StringKey;
  href: string;
  icon?: LucideIcon;
  items?: { title: StringKey; href: string }[];
  badge?: NavBadge;
}

export interface NavGroup {
  /** Empty for the footer group, which is rendered without a heading. */
  title: StringKey | '';
  items: NavItem[];
  /** Rendered after a spacer, pinned to the bottom of the sidebar. */
  footer?: boolean;
}

export const navGroups: NavGroup[] = [
  {
    title: 'nav.group.overview',
    items: [
      { title: 'nav.dashboard', href: '/', icon: LayoutDashboardIcon },
      { title: 'nav.analytics', href: '/analytics', icon: LineChartIcon },
    ],
  },
  {
    title: 'nav.group.design',
    items: [
      { title: 'nav.modules', href: '/modules', icon: BoxesIcon },
      {
        title: 'nav.questions',
        href: '/questions',
        icon: HelpCircleIcon,
        items: [
          { title: 'nav.questions.all', href: '/questions' },
          { title: 'nav.questions.types', href: '/questions/types' },
        ],
      },
      { title: 'nav.scoring', href: '/scoring', icon: SlidersHorizontalIcon },
      // M1. In THIS group and not in Overview: an item analysis is evidence
      // about the instrument, which is a design question — Analytics next door
      // describes cohorts, and the two would be read for each other if they sat
      // together. Every module card also links straight in with `?moduleId=`.
      { title: 'nav.itemAnalysis', href: '/item-analysis', icon: MicroscopeIcon },
      { title: 'nav.personas', href: '/personas', icon: UsersRoundIcon },
    ],
  },
  {
    title: 'nav.group.delivery',
    items: [
      { title: 'nav.candidates', href: '/candidates', icon: UserIcon },
      // Its own item rather than a tab on Candidates: a queue reachable only by
      // knowing where to look is a queue nobody works, and results accumulate
      // withheld in the meantime.
      { title: 'nav.results', href: '/results', icon: ClipboardListIcon },
      // The count is the point of the badge: a hold that nobody is told about
      // accumulates silently, and the queue's failure mode was never that it
      // was wrong — it was that nobody opened it.
      { title: 'nav.qcReview', href: '/qc-review', icon: ShieldCheckIcon, badge: 'qcQueue' },
      { title: 'nav.take', href: '/take', icon: PlayIcon },
    ],
  },
  {
    title: '',
    footer: true,
    items: [
      { title: 'nav.notifications', href: '/notifications', icon: BellIcon },
      { title: 'nav.support', href: '/support', icon: LifeBuoyIcon },
      { title: 'nav.settings', href: '/settings', icon: SettingsIcon },
    ],
  },
];

/**
 * The breadcrumb trail per route (`plan-beta` E).
 *
 * This replaced a flat `CRUMBS: Record<string, StringKey>` whose values were
 * rendered as text and linked nowhere. A trail is structure, so it is structure
 * here: an ordered list of segments, each optionally addressable.
 *
 * **A segment has an `href` only where a screen exists behind it.** "Assessment
 * Design" is a nav grouping with no page of its own, so it is a label; Questions
 * has one, so on `/questions/types` it is a link back. Giving every segment a
 * destination to make the trail feel interactive is how a breadcrumb starts
 * lying about the shape of the app.
 */
export interface Crumb {
  title: StringKey;
  href?: string;
}

export const TRAILS: Record<string, Crumb[]> = {
  '/': [{ title: 'nav.group.overview' }],
  '/analytics': [{ title: 'nav.group.overview' }, { title: 'nav.analytics' }],
  '/modules': [{ title: 'nav.group.design' }, { title: 'nav.modules' }],
  '/questions': [{ title: 'nav.group.design' }, { title: 'nav.questions' }],
  '/questions/types': [
    { title: 'nav.group.design' },
    // Addressable: the item bank is a real screen, and this is the one trail in
    // the console with somewhere to go back to.
    { title: 'nav.questions', href: '/questions' },
    { title: 'nav.questions.types' },
  ],
  '/scoring': [{ title: 'nav.group.design' }, { title: 'nav.scoring' }],
  // Addressable: a module card is where most readers arrive from, and the
  // catalogue behind it is a real screen.
  '/item-analysis': [
    { title: 'nav.group.design' },
    { title: 'nav.modules', href: '/modules' },
    { title: 'nav.itemAnalysis' },
  ],
  '/personas': [{ title: 'nav.group.design' }, { title: 'nav.personas' }],
  '/candidates': [{ title: 'nav.group.delivery' }, { title: 'nav.candidates' }],
  // Addressable: the list behind a candidate is a real screen, so the trail
  // goes back to it rather than merely naming it.
  '/candidates/:candidateRef': [
    { title: 'nav.group.delivery' },
    { title: 'nav.candidates', href: '/candidates' },
    { title: 'candidate.title' },
  ],
  '/qc-review': [{ title: 'nav.group.delivery' }, { title: 'nav.qcReview' }],
  '/take': [{ title: 'nav.group.delivery' }, { title: 'nav.take' }],
  '/results': [{ title: 'nav.group.delivery' }, { title: 'nav.results' }],
  '/settings': [{ title: 'nav.group.platform' }, { title: 'nav.settings' }],
  '/profile': [{ title: 'nav.group.platform' }, { title: 'profile.title' }],
  '/notifications': [{ title: 'nav.group.platform' }, { title: 'nav.notifications' }],
  '/support': [{ title: 'nav.group.platform' }, { title: 'nav.support' }],
};
